import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { ZoomIn, Check, X } from "lucide-react";
import api from "@/lib/api";
import getCroppedImg from "@/lib/cropImage";
import { useI18n } from "@/i18n/I18nContext";

export function ImageCropper({ src, aspect = 1, kind = "image", onCancel, onDone }) {
  const { t } = useI18n();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedImg(src, pixels, aspect === 1 ? 800 : 1600);
      const form = new FormData();
      form.append("file", blob, "crop.jpg");
      const { data } = await api.post(`/files/upload?kind=image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onDone(data);
      toast.success(t("cropper.saved"));
    } catch (e) {
      toast.error(t("cropper.error"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="image-cropper">
      <div className="glass-strong rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="font-display font-semibold text-white">{t("cropper.title")}</h3>
          <button onClick={onCancel} className="text-zinc-400 hover:text-white" data-testid="cropper-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative h-80 bg-black">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            cropShape={aspect === 1 ? "round" : "rect"}
          />
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-3">
            <ZoomIn className="w-4 h-4 text-zinc-400" />
            <input
              type="range" min={1} max={3} step={0.05} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-cyan-500"
              data-testid="cropper-zoom"
            />
          </div>
          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 glass rounded-xl py-2.5 text-white hover:bg-white/10 transition-colors duration-300">{t("cropper.cancel")}</button>
            <button onClick={confirm} disabled={uploading} data-testid="cropper-confirm"
              className="flex-1 bg-brand-gradient rounded-xl py-2.5 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {uploading ? t("cropper.sending") : <><Check className="w-4 h-4" /> {t("cropper.confirm")}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
